import { IContextualMenuItem, mergeStyles } from "@fluentui/react";
import { useEffect, useState } from "react";
import { logEvent, logException } from "../../../helper/AppInsightsHelper";
import { CustomFilterPill } from "../../../SharedComponents/CustomFilterPill";
import { useGetHealthFilterPillDataQuery } from "../../../slices/ExploreHealth.slice";
import { mapFilterPillMenuItemsPredicate } from "../../CustomerSearch/CustomerSearch.common";
import { HEALTH_VIEW_STYLES } from "./HealthView.styles";

const STATIC_PRODUCT_FAMILIES = ["AllUp", "Intune", "MTP", "Purview"];

const PRODUCT_INITIATIVE_MAP: Record<string, string[]> = {
  intune: ["Intune Marquee", "E5 Renewal Initiative", "Intensive Care", "Mission Critical"],
  mtp: ["Catalyst", "E5 Renewal Initiative", "Intensive Care", "Mission Critical"],
  purview: ["Purview BwC", "E5 Renewal Initiative", "Intensive Care", "Mission Critical"],
};

export const HealthViewFilterPill = (props: any) => {
  const defaultValue = "All";

  const availableProductFamilies = props.hideIntune
    ? STATIC_PRODUCT_FAMILIES.filter((pf) => pf !== "Intune")
    : STATIC_PRODUCT_FAMILIES;

  const [productFamily] = useState<IContextualMenuItem[]>(
    availableProductFamilies.map(mapFilterPillMenuItemsPredicate)
  );
  const [selectedProductFamily, setSelectedProductFamily] = useState<string[]>([]);
  const [regions, setRegions] = useState<IContextualMenuItem[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [segmentGroups, setSegmentGroups] = useState<IContextualMenuItem[]>([]);
  const [selectedSegmentGroups, setSelectedSegmentGroups] = useState<string[]>([]);
  const [segments, setSegments] = useState<IContextualMenuItem[]>([]);
  const [selectedSegments, setSelectedSegments] = useState<string[]>([]);
  const [subSegments, setSubSegments] = useState<IContextualMenuItem[]>([]);
  const [selectedSubSegments, setSelectedSubSegments] = useState<string[]>([]);
  const [isS500, setIsS500] = useState<IContextualMenuItem[]>([]);
  const [selectedIsS500, setSelectedIsS500] = useState<string[]>([]);
  const [industryValue, setIndustryValue] = useState<IContextualMenuItem[]>([]);
  const [selectedIndustryValue, setSelectedIndustryValue] = useState<string[]>([]);
  const [agreementEndQuarter, setAgreementEndQuarter] = useState<IContextualMenuItem[]>([]);
  const [selectedAgreementEndQuarter, setSelectedAgreementEndQuarter] = useState<string[]>([]);
  const [initiatives, setInitiatives] = useState<IContextualMenuItem[]>([]);
  const [selectedInitiatives, setSelectedInitiatives] = useState<string[]>([]);
  const [subInitiatives, setSubInitiatives] = useState<IContextualMenuItem[]>([]);
  const [selectedSubInitiatives, setSelectedSubInitiatives] = useState<string[]>([]);
  const [isSubInitiativesDisabled, setIsSubInitiativesDisabled] = useState<boolean>(false);
  const { data, error } = useGetHealthFilterPillDataQuery(null);
  const [allData, setAllData] = useState<any>();
  const filterPillsData = [
    {
      key: "healthDataFilterPillForProductFamily",
      name: "Product Family",
      items: productFamily,
      urlParamKey: "productFamily",
      setSelectedItems: setSelectedProductFamily,
      singleSelect: true // Flag to identify single-select behavior
    },
    {
      key: "healthDataFilterPillForInitiatives",
      name: "Initiatives",
      items: initiatives,
      setSelectedItems: setSelectedInitiatives,
      urlParamKey: "initiatives"
    },
    {
      key: "healthDataFilterPillForSubInitiatives",
      name: "Sub Initiatives",
      items: subInitiatives,
      setSelectedItems: setSelectedSubInitiatives,
      urlParamKey: "subInitiatives",
      disabled: isSubInitiativesDisabled
    },
    {
      key: "healthDataFilterPillForRegion",
      name: "Region",
      items: regions,
      urlParamKey: "region",
      setSelectedItems: setSelectedRegions
    },
    {
      key: "healthDataFilterPillForSegmentGroup",
      name: "Segment Group",
      items: segmentGroups,
      urlParamKey: "segmentGroups",
      setSelectedItems: setSelectedSegmentGroups
    },
    {
      key: "healthDataFilterPillForSegment",
      name: "Segment",
      items: segments,
      urlParamKey: "segment",
      setSelectedItems: setSelectedSegments
    },
    {
      key: "healthDataFilterPillForSubSegment",
      name: "Sub Segment",
      items: subSegments,
      urlParamKey: "subSegment",
      setSelectedItems: setSelectedSubSegments
    },
    {
      key: "healthDataFilterPillForIsS500",
      name: "IsS500",
      items: isS500,
      urlParamKey: "isS500",
      setSelectedItems: setSelectedIsS500
    },
    {
      key: "healthDataFilterPillForIndustry",
      name: "Industry",
      items: industryValue,
      setSelectedItems: setSelectedIndustryValue,
      urlParamKey: "industry"
    },
    {
      key: "healthDataFilterPillForAgreementEndQuarter",
      name: "Agreement End Quarter",
      items: agreementEndQuarter,
      setSelectedItems: setSelectedAgreementEndQuarter,
      urlParamKey: "agreementEndQuarter"
    }
  ];

  useEffect(() => {
    const customProps = {
      HealthViewFilters: props
    };
    if (data) {
      setAllData(data);
      const initiativesValue: Set<string> = new Set();
      const subInitiativesValue: Set<string> = new Set();

      data?.initiativesAndSubInitiatives?.forEach((item: any) => {
        initiativesValue.add(item.initiatives);
        item.subInitiatives?.forEach((subInitiative: string) => {
          subInitiativesValue.add(subInitiative);
        });
      });

      setRegions(((data?.region as string[]) || []).map(mapFilterPillMenuItemsPredicate));
      setSegmentGroups(((data?.segmentGroup as string[]) || []).map(mapFilterPillMenuItemsPredicate));
      setSegments(((data?.segment as string[]) || []).map(mapFilterPillMenuItemsPredicate));
      setSubSegments(((data?.subSegment as string[]) || []).map(mapFilterPillMenuItemsPredicate));
      setIsS500(["Yes", "No"].map(mapFilterPillMenuItemsPredicate));
      setIndustryValue(((data?.industry as string[]) || []).map(mapFilterPillMenuItemsPredicate));
      setAgreementEndQuarter(((data?.agreementEndQuarter as string[]) || []).filter((v: string) => v).map(mapFilterPillMenuItemsPredicate));
      setInitiatives(Array.from(initiativesValue).map(mapFilterPillMenuItemsPredicate));
      setSubInitiatives(Array.from(subInitiativesValue).map(mapFilterPillMenuItemsPredicate));
      logEvent("useGetHealthFilterPillDataQuery API call success", "Health View", "FilterPillHealth", customProps);
    }
    if (error) {
      logException("Health View", "FilterPillHealth", "useGetHealthFilterPillDataQuery", error, customProps);
    }
  }, [data]);
  useEffect(() => {
    // For single-select Product Family, directly use the value without semicolon concatenation
    let newProductFamilyValue: string = "";
    if (selectedProductFamily.length > 0) {
      newProductFamilyValue = selectedProductFamily[0];
    }
    if (!selectedProductFamily || selectedProductFamily.length === 0) {
      const subInitiativesValue: Set<string> = new Set();
      const initiativesValue: Set<string> = new Set();
      data?.initiativesAndSubInitiatives?.forEach((item: any) => {
        initiativesValue.add(item.initiatives);
        item.subInitiatives?.forEach((subInitiative: string) => {
          subInitiativesValue.add(subInitiative);
        });
      });
      setInitiatives(Array.from(initiativesValue).map(mapFilterPillMenuItemsPredicate));

      // Cascade: if initiatives are selected, only show their sub-initiatives
      if (selectedInitiatives && selectedInitiatives.length > 0) {
        const filteredSubInitiatives: Set<string> = new Set();
        data?.initiativesAndSubInitiatives?.forEach((item: any) => {
          if (selectedInitiatives.includes(item.initiatives)) {
            item.subInitiatives?.forEach((subInitiative: string) => {
              filteredSubInitiatives.add(subInitiative);
            });
          }
        });
        const subInitiativesArray = Array.from(filteredSubInitiatives);
        setSubInitiatives(subInitiativesArray.map(mapFilterPillMenuItemsPredicate));
        setIsSubInitiativesDisabled(subInitiativesArray.length === 1 && subInitiativesArray[0] === "Not Applicable");
      } else {
        setSubInitiatives(Array.from(subInitiativesValue).map(mapFilterPillMenuItemsPredicate));
        setIsSubInitiativesDisabled(false);
      }
    } else {
      const filteredInitiatives: Set<string> = new Set();
      const filteredSubInitiatives: Set<string> = new Set();

      // Determine which initiative names are allowed for the selected product family
      const selectedFamily = selectedProductFamily[0]?.toLowerCase() ?? "";
      const allowedInitiatives = PRODUCT_INITIATIVE_MAP[selectedFamily];

      allData?.initiativesAndSubInitiatives?.forEach((item: any) => {
        const initiativeName: string = item.initiatives ?? "";
        // If there's a mapping for this product family, only include allowed initiatives;
        // otherwise (e.g. AllUp) show all.
        if (!allowedInitiatives || allowedInitiatives.some((allowed) => allowed.toLowerCase() === initiativeName.toLowerCase())) {
          filteredInitiatives.add(initiativeName);
        }
      });

      // Cascade: if initiatives are selected, only show their sub-initiatives
      if (selectedInitiatives && selectedInitiatives.length > 0) {
        allData?.initiativesAndSubInitiatives?.forEach((item: any) => {
          if (selectedInitiatives.includes(item.initiatives)) {
            item.subInitiatives?.forEach((subInitiative: string) => {
              filteredSubInitiatives.add(subInitiative);
            });
          }
        });
      } else {
        allData?.initiativesAndSubInitiatives?.forEach((item: any) => {
          item.subInitiatives?.forEach((subInitiative: string) => {
            filteredSubInitiatives.add(subInitiative);
          });
        });
      }

      setInitiatives(Array.from(filteredInitiatives).map(mapFilterPillMenuItemsPredicate));
      const subInitiativesArray = Array.from(filteredSubInitiatives);
      setSubInitiatives(subInitiativesArray.map(mapFilterPillMenuItemsPredicate));

      // Disable subInitiatives filter if it only contains "Not Applicable"
      if (subInitiativesArray.length === 1 && subInitiativesArray[0] === "Not Applicable") {
        setIsSubInitiativesDisabled(true);
      } else {
        setIsSubInitiativesDisabled(false);
      }
    }
    let newRegionValue: string = "";
    selectedRegions.forEach((element: string) => {
      newRegionValue = element + ";" + newRegionValue;
    });

    let newSegmentGroupValue: string = "";
    selectedSegmentGroups.forEach((element: string) => {
      newSegmentGroupValue = element + ";" + newSegmentGroupValue;
    });
    let newSegmentValue: string = "";
    selectedSegments.forEach((element: string) => {
      newSegmentValue = element + ";" + newSegmentValue;
    });
    let newSubSegmentValue: string = "";
    selectedSubSegments.forEach((element: string) => {
      newSubSegmentValue = element + ";" + newSubSegmentValue;
    });
    let newIsS500Value: string = "";
    selectedIsS500.forEach((element: string | string[]) => {
      newIsS500Value = selectedIsS500.length > 1 ? "" : element.includes("Yes") ? "1" : "0";
    });
    let newIndustryValue: string = "";
    selectedIndustryValue.forEach((element: string) => {
      newIndustryValue = element + ";" + newIndustryValue;
    });
    let newAgreementEndQuarterValue: string = "";
    selectedAgreementEndQuarter.forEach((element: string) => {
      newAgreementEndQuarterValue = element + ";" + newAgreementEndQuarterValue;
    });
    let newInitiativesValue: string = "";
    selectedInitiatives.forEach((element: string) => {
      newInitiativesValue = element + ";" + newInitiativesValue;
    });
    let newSubInitiativesValue: string = "";
    selectedSubInitiatives.forEach((element: string) => {
      newSubInitiativesValue = element + ";" + newSubInitiativesValue;
    });
    props.onProductFamilyChange(newProductFamilyValue);
    props.onRegionChange(newRegionValue);
    props.onSegmentGroupChange(newSegmentGroupValue);
    props.onSegmentChange(newSegmentValue);
    props.onSubSegmentChange(newSubSegmentValue);
    props.onIsS500Change(newIsS500Value);
    props.onIndustryChange(newIndustryValue);
    props.onInitiativesChange(newInitiativesValue);
    props.onSubInitiativesChange(newSubInitiativesValue);
    props.onAgreementEndQuarterChange(newAgreementEndQuarterValue);
  }, [
    selectedProductFamily,
    selectedRegions,
    selectedSegmentGroups,
    selectedSegments,
    selectedSubSegments,
    selectedIsS500,
    selectedIndustryValue,
    selectedAgreementEndQuarter,
    selectedInitiatives,
    selectedSubInitiatives
  ]);

  return (
    <div
      className={mergeStyles({
        alignItems: "flex-start",
        display: "flex",
        flexWrap: "nowrap",
        justifyContent: "space-between",
        padding: 12
      })}
    >
      <div style={{ display: "flex", flexWrap: "wrap", flex: 1 }}>
        {filterPillsData.map((pill) => (
          <CustomFilterPill
            defaultValue={defaultValue}
            isRemovable
            key={pill.key}
            name={pill.name}
            urlParamKey={pill.urlParamKey}
            singleSelect={(pill as any).singleSelect || false} // Pass singleSelect flag to CustomFilterPill
            menuProps={{
              focusZoneProps: {
                /* Allow up and down arrows to move focus out of the SearchBox */
                shouldInputLoseFocusOnArrowKey: () => true
              },
              items: pill.items
            }}
            onApply={(selectedItems) => pill.setSelectedItems(selectedItems || [])}
            onCancel={(previouslyselectedItem) => pill.setSelectedItems(previouslyselectedItem || [])}
            onResetFilters={() => pill.setSelectedItems([])}
            styles={HEALTH_VIEW_STYLES.HEALTH_VIEW_FILTER_PILL.STYLES}
          />
        ))}
      </div>
    </div>
  );
};
